import { defineStore } from 'pinia'
import { createConsumer } from '@rails/actioncable'

const POLL_MS = 20_000
const SEND_TIMEOUT_MS = 15_000
const NEAR_BOTTOM_PX = 80

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

const sortByLastMessage = (list) =>
  [...list].sort((a, b) => {
    const ta = a.last_message_at ? Date.parse(a.last_message_at) : 0
    const tb = b.last_message_at ? Date.parse(b.last_message_at) : 0
    return tb - ta
  })

export const useInboxStore = defineStore('inbox', {
  state: () => ({
    conversations: [],
    selectedId: null,
    selected: null,            // { ...summary, messages: [...], notes: [...] }
    unreadCount: 0,
    connectionMode: 'connecting', // 'connecting' | 'live' | 'polling' | 'offline'
    _consumer: null,
    _subscription: null,
    _pollTimer: null,
    _failureCount: 0,
    quickReplies: [],
    _quickRepliesLoaded: false,
    cursor:      null,
    hasMore:     true,
    loadingMore: false,
    activeFilter: { pipeline: '', channel: [], unread: null, q: '' },
  }),

  getters: {
    byId: (s) => (id) => s.conversations.find((c) => c.id === id) || null,
  },

  actions: {
    // ---- hydration from page props ----
    hydrateFromProps({ conversations, selected, unreadCount, hasMore, filters } = {}) {
      if (filters) {
        this.activeFilter = {
          pipeline: filters.pipeline || '',
          channel:  Array.isArray(filters.channel) ? filters.channel.map(String) : [],
          unread:   filters.unread || null,
          q:        filters.q || '',
        }
      }
      if (Array.isArray(conversations)) {
        this.conversations = sortByLastMessage(conversations)
        this.cursor  = conversations.at(-1)?.last_message_at || null
        this.hasMore = hasMore !== undefined ? !!hasMore : conversations.length >= 50
      }
      if (selected) {
        this.setSelected(selected)
      } else if (selected === null) {
        this.selectedId = null
        this.selected = null
      }
      if (typeof unreadCount === 'number') {
        this.unreadCount = unreadCount
      }
    },

    _matchesFilter(summary) {
      const f = this.activeFilter
      if (f.pipeline && summary.pipeline_stage !== f.pipeline) return false
      if (f.channel.length && !f.channel.includes(String(summary.chatwoot_inbox_id))) return false
      if (f.unread === '1' && !(summary.unread > 0)) return false
      // q (search) is server-side fuzzy match — can't replicate client-side cheaply,
      // so we accept anything when q is set and let the next reload reconcile.
      return true
    },

    _removeFromList(id) {
      const idx = this.conversations.findIndex((c) => c.id === id)
      if (idx === -1) return
      const next = [...this.conversations]
      next.splice(idx, 1)
      this.conversations = next
    },

    setSelected(detail) {
      if (!detail) {
        this.selectedId = null
        this.selected = null
        return
      }
      this.selectedId = detail.id
      this.selected = { ...detail, messages: detail.messages || [] }
      // ensure list summary mirrors zeroed unread
      this._mergeIntoList({ ...detail, unread: 0 })
      this._recomputeUnread()
    },

    // ---- in-list maintenance ----
    _mergeIntoList(summary) {
      const idx = this.conversations.findIndex((c) => c.id === summary.id)
      if (idx === -1) {
        this.conversations = sortByLastMessage([summary, ...this.conversations])
      } else {
        const merged = { ...this.conversations[idx], ...summary }
        const next = [...this.conversations]
        next[idx] = merged
        this.conversations = sortByLastMessage(next)
      }
    },

    applyConversation(summary) {
      if (!summary?.id) return
      if (this._matchesFilter(summary)) {
        this._mergeIntoList(summary)
      } else {
        this._removeFromList(summary.id)
      }
      if (this.selectedId === summary.id && this.selected) {
        this.selected = { ...this.selected, ...summary, unread: 0, messages: this.selected.messages }
      }
      this._recomputeUnread()
    },

    applyMessage({ conversation, message } = {}) {
      if (!conversation?.id || !message?.id) return
      const summary = this.selectedId === conversation.id
        ? { ...conversation, unread: 0 }
        : conversation
      if (this._matchesFilter(summary)) {
        this._mergeIntoList(summary)
      } else {
        this._removeFromList(summary.id)
      }

      if (this.selectedId === conversation.id && this.selected) {
        const list = this.selected.messages
        const byId = list.findIndex((m) => m.id === message.id)
        const byEcho = message.echo_id
          ? list.findIndex((m) => m.echo_id && m.echo_id === message.echo_id)
          : -1
        if (byId !== -1) {
          list[byId] = { ...list[byId], ...message, status: 'sent' }
        } else if (byEcho !== -1) {
          list[byEcho] = { ...list[byEcho], ...message, status: 'sent' }
        } else {
          list.push({ ...message, status: 'sent' })
        }
      }

      this._recomputeUnread()
    },

    _recomputeUnread() {
      // best-effort sum; the inertia_share value is authoritative on visit
      const sum = this.conversations.reduce((n, c) => n + (c.unread || 0), 0)
      this.unreadCount = sum
    },

    markOptimisticRead(id) {
      const idx = this.conversations.findIndex((c) => c.id === id)
      if (idx === -1) return
      const next = [...this.conversations]
      next[idx] = { ...next[idx], unread: 0 }
      this.conversations = next
      this._recomputeUnread()
    },

    // ---- send flow ----
    async sendMessage(input) {
      const opts = typeof input === 'string' ? { content: input } : (input || {})
      const trimmed = (opts.content || '').trim()
      const attachment = opts.attachment || null
      if (!trimmed && !attachment) return null
      if (!this.selected) throw new Error('no_selected')

      const tempId = `temp-${crypto.randomUUID()}`
      const echoId = crypto.randomUUID()
      const previewUrl = attachment ? URL.createObjectURL(attachment) : null
      const temp = {
        id: tempId,
        echo_id: echoId,
        direction: 'outgoing',
        content: trimmed,
        content_type: 'text',
        sender_kind: 'agent',
        attachments: attachment
          ? [ { idx: 0, file_name: attachment.name, file_type: attachment.type, url: previewUrl } ]
          : [],
        sent_at: new Date().toISOString(),
        status: 'sending',
      }
      this.selected.messages.push(temp)

      const timer = setTimeout(() => {
        const t = this.selected?.messages.find((m) => m.id === tempId)
        if (t && t.status === 'sending') {
          t.status = 'failed'
          t.error = 'timeout'
        }
      }, SEND_TIMEOUT_MS)

      try {
        let res
        if (attachment) {
          const fd = new FormData()
          fd.append('content', trimmed)
          fd.append('echo_id', echoId)
          fd.append('attachment', attachment)
          res = await fetch(`/inbox/conversations/${this.selectedId}/messages`, {
            method: 'POST',
            headers: {
              'Accept':            'application/json',
              'X-CSRF-Token':      csrfToken(),
              'X-Requested-With':  'XMLHttpRequest',
            },
            body: fd,
          })
        } else {
          res = await fetch(`/inbox/conversations/${this.selectedId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type':      'application/json',
              'Accept':            'application/json',
              'X-CSRF-Token':      csrfToken(),
              'X-Requested-With':  'XMLHttpRequest',
            },
            body: JSON.stringify({ content: trimmed, echo_id: echoId }),
          })
        }

        const data = await res.json().catch(() => ({}))
        clearTimeout(timer)

        if (!res.ok || !data.ok) {
          const t = this.selected?.messages.find((m) => m.id === tempId)
          if (t) {
            t.status = 'failed'
            t.error = data.error || 'send_failed'
          }
          if (previewUrl) URL.revokeObjectURL(previewUrl)
          return null
        }

        // Reconcile temp with server message. If the cable broadcast already
        // beat us and inserted the same row by id/echo_id, just drop the temp.
        const list = this.selected.messages
        const tempIdx    = list.findIndex((m) => m.id === tempId)
        const presentIdx = list.findIndex(
          (m) => m.id !== tempId && (m.id === data.message.id || (data.message.echo_id && m.echo_id === data.message.echo_id)),
        )
        if (tempIdx !== -1 && presentIdx !== -1) {
          list.splice(tempIdx, 1)
        } else if (tempIdx !== -1) {
          list[tempIdx] = { ...data.message, status: 'sent' }
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        return data.message
      } catch (e) {
        clearTimeout(timer)
        const t = this.selected?.messages.find((m) => m.id === tempId)
        if (t) {
          t.status = 'failed'
          t.error = e.message || 'network_error'
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        return null
      }
    },

    async shareAgreement(conversationId, rentalId) {
      try {
        const res = await fetch(`/inbox/conversations/${conversationId}/share_agreement`, {
          method: 'POST',
          headers: {
            'Content-Type':      'application/json',
            'Accept':            'application/json',
            'X-CSRF-Token':      csrfToken(),
            'X-Requested-With':  'XMLHttpRequest',
          },
          body: JSON.stringify({ rental_id: rentalId }),
        })
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.ok) return { ok: true, message: data.message }
        return { ok: false, error: data.error || `http_${res.status}` }
      } catch (e) {
        return { ok: false, error: e.message || 'network_error' }
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore || !this.cursor) return
      this.loadingMore = true
      try {
        const url = new URL('/inbox.json', window.location.origin)
        url.searchParams.set('before', this.cursor)
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
        if (!res.ok) return
        const data = await res.json()
        if (Array.isArray(data.conversations)) {
          const seen = new Set(this.conversations.map((c) => c.id))
          this.conversations = sortByLastMessage([
            ...this.conversations,
            ...data.conversations.filter((c) => !seen.has(c.id)),
          ])
          this.hasMore = !!data.has_more
          this.cursor  = data.conversations.at(-1)?.last_message_at || this.cursor
        }
      } finally {
        this.loadingMore = false
      }
    },

    retryMessage(tempId) {
      const m = this.selected?.messages.find((x) => x.id === tempId)
      if (!m || m.status !== 'failed') return
      // remove the failed bubble and re-send fresh
      this.selected.messages = this.selected.messages.filter((x) => x.id !== tempId)
      return this.sendMessage(m.content)
    },

    // ---- quick replies ----
    async loadQuickReplies() {
      if (this._quickRepliesLoaded) return
      try {
        const res = await fetch('/quick_replies.json', { headers: { 'Accept': 'application/json' } })
        if (!res.ok) return
        const data = await res.json()
        this.quickReplies = Array.isArray(data) ? data : []
        this._quickRepliesLoaded = true
      } catch {
        /* leave empty; user can refresh */
      }
    },

    // ---- customer link / unlink ----
    async linkCustomer(conversationId, customerId) {
      return this._linkContact(conversationId, { customer_id: customerId })
    },

    async linkSupplier(conversationId, supplierId) {
      return this._linkContact(conversationId, { vehicle_source_id: supplierId })
    },

    async _linkContact(conversationId, body) {
      const res = await fetch(`/inbox/conversations/${conversationId}/link`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Accept':        'application/json',
          'X-CSRF-Token':  csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this.setSelected(data.conversation)
      }
      return data
    },

    async unlinkCustomer(conversationId) {
      const res = await fetch(`/inbox/conversations/${conversationId}/link`, {
        method: 'DELETE',
        headers: {
          'Accept':        'application/json',
          'X-CSRF-Token':  csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this.setSelected(data.conversation)
      }
      return data
    },

    // ---- notes ----
    async addNote(conversationId, body) {
      if (!body.trim()) return null
      const tempId = `temp-note-${crypto.randomUUID()}`
      const temp = {
        id: tempId,
        body: body.trim(),
        author_id: null,
        author_name: null,
        created_at: new Date().toISOString(),
        status: 'sending',
      }
      if (this.selected && this.selectedId === conversationId) {
        this.selected.notes = [...(this.selected.notes || []), temp]
      }

      try {
        const res = await fetch(`/inbox/conversations/${conversationId}/notes`, {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Accept':        'application/json',
            'X-CSRF-Token':  csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ body: body.trim() }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.ok) {
          this._failNote(tempId)
          return null
        }
        if (this.selected && this.selectedId === conversationId) {
          const list = this.selected.notes || []
          const tempIdx    = list.findIndex((n) => n.id === tempId)
          const presentIdx = list.findIndex((n) => n.id === data.note.id)
          if (tempIdx !== -1 && presentIdx !== -1 && presentIdx !== tempIdx) {
            list.splice(tempIdx, 1)
          } else if (tempIdx !== -1) {
            list[tempIdx] = data.note
          }
        }
        return data.note
      } catch {
        this._failNote(tempId)
        return null
      }
    },

    _failNote(tempId) {
      if (!this.selected) return
      const idx = (this.selected.notes || []).findIndex((n) => n.id === tempId)
      if (idx !== -1) this.selected.notes[idx] = { ...this.selected.notes[idx], status: 'failed' }
    },

    async removeNote(conversationId, noteId) {
      if (!this.selected) return false
      const list = this.selected.notes || []
      const idx = list.findIndex((n) => n.id === noteId)
      const backup = idx !== -1 ? list[idx] : null
      if (idx !== -1) list.splice(idx, 1)
      try {
        const res = await fetch(`/inbox/notes/${noteId}`, {
          method: 'DELETE',
          headers: {
            'Accept':        'application/json',
            'X-CSRF-Token':  csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        if (!res.ok) {
          if (backup) list.splice(idx, 0, backup)
          return false
        }
        return true
      } catch {
        if (backup) list.splice(idx, 0, backup)
        return false
      }
    },

    async takeConversation(conversationId) {
      const res = await fetch(`/inbox/conversations/${conversationId}/take`, {
        method: 'POST',
        headers: {
          'Accept':            'application/json',
          'X-CSRF-Token':      csrfToken(),
          'X-Requested-With':  'XMLHttpRequest',
        },
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok && data.conversation) {
        this.setSelected(data.conversation)
      }
      return res.ok && data.ok
    },

    async releaseConversation(conversationId) {
      const res = await fetch(`/inbox/conversations/${conversationId}/take`, {
        method: 'DELETE',
        headers: {
          'Accept':            'application/json',
          'X-CSRF-Token':      csrfToken(),
          'X-Requested-With':  'XMLHttpRequest',
        },
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok && data.conversation) {
        this.setSelected(data.conversation)
      }
      return res.ok && data.ok
    },

    applyNote({ conversation_id, note } = {}) {
      if (!note?.id) return
      if (this.selectedId !== conversation_id || !this.selected) return
      const list = this.selected.notes || []
      if (list.some((n) => n.id === note.id)) return
      this.selected.notes = [...list, note]
    },

    applyNoteDeleted({ conversation_id, note_id } = {}) {
      if (this.selectedId !== conversation_id || !this.selected) return
      this.selected.notes = (this.selected.notes || []).filter((n) => n.id !== note_id)
    },

    // ---- cable lifecycle ----
    connect() {
      if (this._consumer) return
      this.connectionMode = 'connecting'
      this._consumer = createConsumer()
      this._subscription = this._consumer.subscriptions.create(
        { channel: 'InboxChannel' },
        {
          connected: () => {
            this._failureCount = 0
            this.connectionMode = 'live'
            this._stopPolling()
          },
          disconnected: () => {
            this._failureCount += 1
            if (this._failureCount >= 2) this._startPolling()
          },
          received: (data) => {
            if (!data || !data.type) return
            if (data.type === 'message')           this.applyMessage(data)
            else if (data.type === 'conversation') this.applyConversation(data.conversation)
            else if (data.type === 'note')         this.applyNote(data)
            else if (data.type === 'note_deleted') this.applyNoteDeleted(data)
          },
        },
      )
    },

    disconnect() {
      this._subscription?.unsubscribe()
      this._consumer?.disconnect()
      this._subscription = null
      this._consumer = null
      this._stopPolling()
    },

    _startPolling() {
      if (this._pollTimer) return
      this.connectionMode = 'polling'
      // TODO(phase-2): preserve current filter state (status/inbox_id/q) when polling,
      // otherwise the conversation list silently reverts to the unfiltered top-100.
      this._pollTimer = setInterval(async () => {
        try {
          const url = new URL('/inbox.json', window.location.origin)
          const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
          if (!res.ok) return
          const data = await res.json()
          if (Array.isArray(data.conversations)) {
            this.conversations = sortByLastMessage(data.conversations)
            this._recomputeUnread()
          }
        } catch {
          this.connectionMode = 'offline'
        }
      }, POLL_MS)
    },

    _stopPolling() {
      if (!this._pollTimer) return
      clearInterval(this._pollTimer)
      this._pollTimer = null
    },
  },
})
